/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  promotionalService,
  type AddPromotionalContent,
} from "@src/@api-services/promotional-content";
import type {
  IPromotionalContent,
  IPromotionalContentSearchOptions,
} from "../types";

interface PromotionalContentState {
  data: IPromotionalContent[];
  total: number;
  params: IPromotionalContentSearchOptions;
  selectedPromotionalContent: IPromotionalContent | null;
  loading: boolean;
  detailLoading: boolean;
  actionLoading: boolean;
  error: string | null;
}

export const getPromotionalContent = createAsyncThunk(
  "promotionalContent/getPromotionalContent",
  async (params: IPromotionalContentSearchOptions, { rejectWithValue }) => {
    try {
      const response = await promotionalService.getPromotionalContent(params);
      return {
        params,
        data: response.data.data.data as IPromotionalContent[],
        total: response.data.data.totalRecord as number,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch promotional content"
      );
    }
  }
);

export const getPromotionalContentDetails = createAsyncThunk(
  "promotionalContent/getPromotionalContentDetails",
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await promotionalService.getPromotionalContentDetails(id);
      return response.data.data as IPromotionalContent;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch promotional content details"
      );
    }
  }
);

export const addPromotionalContent = createAsyncThunk(
  "promotionalContent/addPromotionalContent",
  async (data: AddPromotionalContent, { dispatch, getState, rejectWithValue }) => {
    try {
      const response = await promotionalService.addPromotionalContent(data);
      const state = getState() as any;
      await dispatch(getPromotionalContent(state.promotionalContent.params));
      return response.data.data as IPromotionalContent;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to add promotional content"
      );
    }
  }
);

export const updatePromotionalContent = createAsyncThunk(
  "promotionalContent/updatePromotionalContent",
  async (
    {
      id,
      promotionalData,
    }: { id: string | number; promotionalData: Partial<AddPromotionalContent> },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const response = await promotionalService.updatePromotionalContent(
        id,
        promotionalData
      );
      const state = getState() as any;
      await dispatch(getPromotionalContent(state.promotionalContent.params));
      return response.data.data as IPromotionalContent;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update promotional content"
      );
    }
  }
);

export const deletePromotionalContent = createAsyncThunk(
  "promotionalContent/deletePromotionalContent",
  async (id: number, { dispatch, getState, rejectWithValue }) => {
    try {
      await promotionalService.deletePromotionalContent(id);
      const state = getState() as any;
      await dispatch(getPromotionalContent(state.promotionalContent.params));
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete promotional content"
      );
    }
  }
);

const initialState: PromotionalContentState = {
  data: [],
  total: 0,
  params: {
    page: 1,
    pageSize: 10,
    orderBy: "priorityOrder",
    orderDirection: "ASC",
  },
  selectedPromotionalContent: null,
  loading: false,
  detailLoading: false,
  actionLoading: false,
  error: null,
};

export const promotionalContentSlice = createSlice({
  name: "promotionalContent",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedPromotionalContent: (state) => {
      state.selectedPromotionalContent = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPromotionalContent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPromotionalContent.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
        state.total = action.payload.total;
        state.params = action.payload.params;
      })
      .addCase(getPromotionalContent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.data = [];
        state.total = 0;
      })
      .addCase(getPromotionalContentDetails.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(getPromotionalContentDetails.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.selectedPromotionalContent = action.payload;
      })
      .addCase(getPromotionalContentDetails.rejected, (state) => {
        state.detailLoading = false;
        state.selectedPromotionalContent = null;
      })
      .addCase(addPromotionalContent.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(addPromotionalContent.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(addPromotionalContent.rejected, (state) => {
        // Keep list error banner clear — drawer shows toast (roles/permissions pattern).
        state.actionLoading = false;
      })
      .addCase(updatePromotionalContent.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(updatePromotionalContent.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(updatePromotionalContent.rejected, (state) => {
        state.actionLoading = false;
      })
      .addCase(deletePromotionalContent.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(deletePromotionalContent.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(deletePromotionalContent.rejected, (state) => {
        state.actionLoading = false;
      });
  },
});

export const { clearError, clearSelectedPromotionalContent } =
  promotionalContentSlice.actions;

export default promotionalContentSlice.reducer;
